import React, { Dispatch, useEffect, useReducer, useState } from 'react'
import { useParams } from 'react-router-dom'
import moment from 'moment'
import { isNaN, isNil } from 'lodash'
import Cookies from 'js-cookie'
import { ChipsSlider, GhostInput, Button } from '@collegebacker/backer-ui/ui'

import { ScreenHeader } from 'components'
import { BottomSheetTrigger } from 'components/BottomSheetTrigger'
import { Forecast, NumberFormat } from 'utils'
import { validateAmount } from 'utils/helpers'
import { hasActivePaymentMethod } from 'utils/user'
import { buildData, isGiftFlow, isStarterGiftFlow } from '../helpers'
import {
  activationDateValue,
  amountValue,
  frequencyValue,
  contributionInProgressValue
} from '../LocalState'
import { ContributionLimits, CurrentFlow, Frequency, Step } from '../types'
import { MoneyValueObject } from 'modules/global/value-objects/money.value-object'
import {
  HIDE_CUSTOM_AMOUNT,
  SET_AMOUNT,
  SET_CUSTOM_AMOUNT,
  SET_FREQUENCY,
  SHOW_CUSTOM_AMOUNT
} from 'constants/actions'
import { GuestType } from 'modules/global/types'
import { ContributionScheduler } from '../components/ContributionScheduler'
import {
  State,
  reducer
} from 'modules/gift/components/gifting-page/GiftSection'

import styles from './ContributionAmountScreen.module.scss'
import config from 'config'

const ContributionAmountScreen = ({
  activeStepDispatch,
  fund,
  user,
  contributionLimits
}: {
  activeStepDispatch: Dispatch<{
    type: 'setActiveStep'
    payload: {
      activeStep: Step
    }
  }>
  fund: {
    first?: string
    yearsUntilCollege: number
    collegeEntryYear?: number
    contributions?: {
      id: string
    }[]
  }
  user: any
  contributionLimits: ContributionLimits
}) => {
  const { flow }: { flow: CurrentFlow } = useParams()

  const data = buildData()

  const [activationDate, setActivationDate] = useState(activationDateValue())
  const [customAmountError, setCustomAmountError] = useState('')

  let initialAmount = 25_00
  let initialFrequency: Frequency = isStarterGiftFlow(flow)
    ? 'onetime'
    : 'monthly'

  if (contributionInProgressValue()) {
    initialAmount =
      frequencyValue() === 'semimonthly' ? amountValue() * 2 : amountValue()
    initialFrequency = frequencyValue()
  }

  const amountInSlider = data.find((item) => item.value === initialAmount)

  const initialState: State = {
    showCustomAmount: amountInSlider ? false : true,
    amount: initialAmount,
    sliderAmount: amountInSlider ? initialAmount : data[0].value,
    customAmount: initialAmount,
    frequency: initialFrequency
  }

  const [state, dispatch] = useReducer(reducer, initialState)
  const { showCustomAmount, amount, frequency } = state

  useEffect(() => {
    dispatch({
      type: SET_AMOUNT,
      payload: { amount: initialAmount }
    })

    dispatch({
      type: SET_FREQUENCY,
      payload: { frequency: initialFrequency }
    })
  }, [])

  const fundHasContributions =
    fund && fund.contributions && fund.contributions.length > 0
  let headerLabel = 'Make a contribution'
  if (!fundHasContributions) headerLabel = 'Make your first contribution'
  if (isGiftFlow(flow)) headerLabel = 'How much would you like to give?'

  return (
    <form
      className={styles.container}
      onSubmit={(event) => {
        event.preventDefault()
        const amount = amountValue()
        const validationError = validateAmount(
          amount,
          frequency,
          contributionLimits
        )
        if (validationError) {
          setCustomAmountError(validationError)
        } else {
          const futureStep = nextStep(user, flow)

          activeStepDispatch({
            type: 'setActiveStep',
            payload: {
              activeStep: futureStep
            }
          })
          contributionInProgressValue(true)
        }
      }}
    >
      <div className={styles.body}>
        <div className={styles.section}>
          <ScreenHeader
            title={headerLabel}
            subtitle={
              <>
                <span>{getDefaultProjection(amount, fund, frequency)}</span>
                <BottomSheetTrigger
                  children={generateProjectionDisclaimer(fund)}
                  className={styles.infoBubble}
                />
              </>
            }
            className="adaptive"
            smallSubtitle={true}
            smallTitle={true}
          />
        </div>
        <div className={styles.sectionSlider}>
          {showCustomAmount ? (
            <div className={styles.customAmountContainer}>
              <button
                onClick={() => dispatch({ type: HIDE_CUSTOM_AMOUNT })}
                type="button"
                className={styles.switchBtnSlider}
              >
                Switch back to slider
              </button>
              <GhostInput
                allowCents={true}
                autoFocus={true}
                type="money"
                name="custom-amount"
                label=""
                value={(amount / 100).toString()}
                errorMessage={customAmountError}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  const amount =
                    Number(event.target.value.replace(/,/g, '')) * 100
                  dispatch({
                    type: SET_CUSTOM_AMOUNT,
                    payload: { amount }
                  })
                }}
              />
            </div>
          ) : (
            <>
              <button
                onClick={() => dispatch({ type: SHOW_CUSTOM_AMOUNT })}
                type="button"
                className={styles.switchBtn}
              >
                Enter a custom amount
              </button>

              <ChipsSlider
                showCaption={false}
                items={data}
                defaultIndex={data.findIndex(
                  (item: any) => item.value === amount
                )}
                onChange={(index) => {
                  const value = data[index].value
                  dispatch({
                    type: SET_AMOUNT,
                    payload: { amount: value }
                  })
                }}
              />
            </>
          )}
        </div>
        <div className={styles.section}>
          <ContributionScheduler
            flow={flow}
            frequency={frequency}
            dispatch={dispatch}
            activationDate={activationDate}
            setActivationDate={setActivationDate}
          />
        </div>
      </div>
      <div className={styles.section}>
        <div className={styles.ctaContainer}>
          <Button
            label="Continue"
            className={styles.continueBtn}
            type="submit"
          />
        </div>
      </div>
    </form>
  )
}

export function getForecastProjection({
  frequency,
  yearsUntilCollege,
  amount,
  currentBalance,
  totalNumberOfPaymentsOverride
}: {
  frequency: Frequency
  yearsUntilCollege: number
  amount: number
  currentBalance?: number
  totalNumberOfPaymentsOverride?: number | undefined
}) {
  return Forecast.calculateContributionGrowth({
    frequency,
    yearsUntilCollege: isNil(yearsUntilCollege) ? 18 : yearsUntilCollege,
    currentBalance: currentBalance || 0,
    contributionAmount: amount,
    totalNumberOfPaymentsOverride
  })
}

export function getForecastProjectionString(projection: MoneyValueObject) {
  if (isNaN(projection.value)) {
    return '$0'
  } else {
    return NumberFormat.currencyFromCents(projection.value, false)
  }
}

export const frequencyTextMap = {
  onetime: 'today could grow into',
  monthly: 'per month could grow to',
  annually: 'per year could grow to'
}

function getDefaultProjection(
  amount: number,
  fund: {
    yearsUntilCollege: number
    collegeEntryYear?: number
  },
  frequency: Frequency
) {
  const newFrequency: 'monthly' | 'annually' | 'onetime' =
    frequency === 'semimonthly' ? 'monthly' : frequency

  const projection = getForecastProjection({
    frequency: newFrequency,
    yearsUntilCollege: fund?.yearsUntilCollege,
    amount
  })
  const projectionString = getForecastProjectionString(projection)

  return `${NumberFormat.currencyFromCents(amount, false)} ${
    frequencyTextMap[newFrequency]
  } ${projectionString} by ${
    fund?.collegeEntryYear || moment().add(18, 'y').year()
  }`
}

export function generateProjectionDisclaimer(
  fund:
    | {
        first?: string
        collegeEntryYear?: number
      }
    | null
    | undefined
): JSX.Element {
  const childName = fund?.first ?? 'the child'
  const entryYear = fund?.collegeEntryYear ?? moment().add(18, 'y').year()

  return (
    <p>
      We assume that {childName} will enroll in college in {entryYear}. The
      projected value of your fund is calculated using historical S&P 500
      returns, taking into account your current balance and scheduled recurring
      contributions. For disclosures and details on our assumptions, please
      visit <a href={`${config.BACKEND_ROOT_URL}/legal`}>backer.com/legal</a>.
    </p>
  )
}

const nextStep: (user: User, flow: CurrentFlow) => Step = (user, flow) => {
  if (!Cookies.get('authorization')) {
    return 'signup'
  }
  if (isStarterGiftFlow(flow)) {
    return 'beneficiary'
  }
  if (hasActivePaymentMethod(user)) {
    return 'confirmation'
  } else {
    return 'add-payment-method'
  }
}

interface User {
  bankAccount: {
    plaidVerificationStatus: string
  }[]
  guest: GuestType
}
export default ContributionAmountScreen
